import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Input, SubmitButton } from '@ixicare/ui';
import { Auth } from '@ixicare/resources';
import { t } from '~/i18n';
import { useStores } from '~store/utils';
import { humaniseErrorToMessage } from '~utils/error';

const MIN_PASSWORD_LENGTH = 8;

const AcceptInviteContainer: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { auth } = useStores();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  const validationError = React.useMemo(() => {
    if (!password.length) return null;
    if (password.length < MIN_PASSWORD_LENGTH) return t('auth.resetPassword.errors.tooShort');
    if (confirmPassword.length && password !== confirmPassword) return t('auth.resetPassword.errors.mismatch');
    return null;
  }, [password, confirmPassword]);

  const canSubmit = !!token && password.length >= MIN_PASSWORD_LENGTH && password === confirmPassword && !loading;

  const handleSubmit = React.useCallback(
    async (e: React.SyntheticEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);
      setError(null);
      try {
        const { email } = await Auth.confirmPasswordReset({ token, password });
        const success = await auth.login(email, password);
        if (success && auth.user?.id) {
          navigate('/dashboard');
          return;
        }
        setError(auth.error ?? new Error('Login failed'));
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    },
    [token, password, auth, navigate]
  );

  if (!token) {
    return (
      <div>
        <Alert type="error">{t('auth.acceptInvite.errors.invalidToken')}</Alert>
      </div>
    );
  }

  return (
    <div>
      <h1 className="mb-10">{t('auth.acceptInvite.title')}</h1>
      <p className="mb-20">{t('auth.acceptInvite.description')}</p>
      <form onSubmit={handleSubmit}>
        {error && <Alert type="error">{humaniseErrorToMessage(error)}</Alert>}
        {validationError && <Alert type="error">{validationError}</Alert>}
        <Input
          label={t('auth.acceptInvite.form.password')}
          value={password}
          type="password"
          className="mb-20"
          onValueChange={setPassword}
        />
        <Input
          label={t('auth.acceptInvite.form.confirmPassword')}
          value={confirmPassword}
          type="password"
          className="mb-20"
          onValueChange={setConfirmPassword}
        />
        <SubmitButton disabled={!canSubmit} loading={loading}>
          {t('auth.acceptInvite.form.submit')}
        </SubmitButton>
      </form>
    </div>
  );
};

export default AcceptInviteContainer;
